import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  Bot,
  ChevronDown,
  ChevronUp,
  FileText,
  MessageSquareMore,
  RefreshCw,
  Trash2,
} from "lucide-react";
import ApplicantShell from "../ApplicantShell";
import ApplicantCoachPanel from "./ApplicantCoachPanel";
import { useApplicantCoach } from "./ApplicantCoachContext";
import config from "../../config/config";
import {
  loadApplicantMessagingWorkspace,
  updateApplicantMessageState,
} from "../../services/applicant/applicantMessagingApi";
import { buildAuthHeaders, clearStoredAuth, getStoredIdentity } from "../../utils/authSession";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const EMPTY_WORKSPACE = {
  items: [],
  folders: { new: [], outbox: [], archive: [] },
  cases: [],
  summary: {
    new_count: 0,
    outbox_count: 0,
    archive_count: 0,
    open_count: 0,
    done_count: 0,
    unread_count: 0,
    overdue_count: 0,
    due_soon_count: 0,
    default_folder: "new",
    summary_lines: [],
  },
  highlights: [],
};

function authHeaders() {
  return buildAuthHeaders();
}

function createDraft() {
  return {
    response_text: "",
    additional_details: "",
    acknowledged: false,
    mark_not_available: false,
    mark_already_fulfilled: false,
    completion_note: "",
    verification_reference_id: null,
    uploaded_reference_label: "",
  };
}

function formatDateTime(value) {
  if (!value) return "Keine Frist";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDateShort(value) {
  if (!value) return "ohne Frist";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatRelativeDue(value) {
  if (!value) return "ohne Frist";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "ohne Frist";
  const diffDays = Math.ceil((parsed.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return `seit ${Math.abs(diffDays)} Tag(en) abgelaufen`;
  if (diffDays === 0) return "heute";
  if (diffDays === 1) return "morgen";
  return `in ${diffDays} Tagen`;
}

function compactText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function stripRequestLabelPrefix(value) {
  return compactText(value)
    .replace(/^Bitte\s+Request:\s*/i, "")
    .replace(/^Request:\s*/i, "")
    .replace(/^Bitte\s+/i, "")
    .replace(/\s+des Bewerbers\b/i, "")
    .trim();
}

function senderTone(senderClass) {
  if (senderClass === "applicant") return "applicant-transaction-chip applicant-transaction-chip--success";
  if (senderClass === "system") return "applicant-transaction-chip applicant-transaction-chip--warning";
  return "applicant-transaction-chip applicant-transaction-chip--status";
}

function toneClass(tone) {
  if (tone === "danger") return "applicant-transaction-chip applicant-transaction-chip--danger";
  if (tone === "warning") return "applicant-transaction-chip applicant-transaction-chip--warning";
  if (tone === "success") return "applicant-transaction-chip applicant-transaction-chip--success";
  return "applicant-transaction-chip applicant-transaction-chip--status";
}

function isDocumentItem(item) {
  return Number(item?.case_id || 0) > 0 || String(item?.source_type || "").startsWith("filing_");
}

function getFolderLabel(folder) {
  if (folder === "outbox") return "Ausgang";
  if (folder === "archive") return "Archiv";
  return "Neu";
}

function getStatusFilterLabel(statusFilter) {
  if (statusFilter === "open") return "Offen";
  if (statusFilter === "done") return "Erledigt";
  return "Alle";
}

function getSenderLabel(item) {
  if (!item) return "Experts4Europe Portal";
  return item.sender_name || item.sender_role || "Experts4Europe Portal";
}

function getDocumentLabel(item) {
  const rawLabel = item?.detail_title || item?.request_title || item?.subject || "";
  return stripRequestLabelPrefix(rawLabel) || "Unterlagennachweis";
}

function getDisplaySubject(item) {
  if (!item) return "Applicant-Nachricht";
  if (isDocumentItem(item)) {
    const label = getDocumentLabel(item);
    const dueLabel = item?.due_at ? ` bis ${formatDateShort(item.due_at)}` : "";
    if (item?.capabilities?.can_respond) return `Bitte ${label}${dueLabel} einreichen`;
    if (item?.due_at) return `${label}${dueLabel} prüfen`;
    return label;
  }
  return compactText(item.subject) || "Applicant-Nachricht";
}

function getDisplayPreview(item) {
  if (!item) return "";
  if (isDocumentItem(item)) {
    if (item?.capabilities?.can_respond) {
      return "Dieser Nachweis ist noch offen. Du kannst direkt eine Rückmeldung senden oder einen Nachweis hochladen.";
    }
    if (item?.due_state === "overdue") {
      return "Dieser Fall ist überfällig. Prüfe jetzt, was fehlt und was du als Nächstes senden oder bestätigen musst.";
    }
    if (item?.due_state === "due_soon" && item?.due_at) {
      return `Dieser Fall braucht bald deine Reaktion. Die Frist läuft ${formatRelativeDue(item.due_at)} ab.`;
    }
    return "Bitte prüfe diesen Fall und öffne ihn bei Bedarf für Details, Frist und nächsten Schritt.";
  }
  return compactText(item.preview) || "Öffne den Eintrag für Details und den nächsten Schritt.";
}

function getDisplayReason(item) {
  if (!item) return "";
  if (isDocumentItem(item)) {
    if (item?.due_at) return `Dieser Fall ist noch offen und hat eine Frist bis ${formatDateShort(item.due_at)}.`;
    return "Dieser Fall ist noch offen und braucht deine Aufmerksamkeit.";
  }
  return compactText(item.detail_reason || item.preview || "");
}

function getDisplayDescription(item) {
  if (!item) return "";
  if (isDocumentItem(item)) {
    if (item?.capabilities?.can_respond) {
      return "Sende eine kurze Rückmeldung oder reiche den angefragten Nachweis direkt in diesem Fall nach.";
    }
    return "Öffne den Fall, damit du die sichtbaren Details, die Frist und den nächsten Schritt prüfen kannst.";
  }
  const description = compactText(item.detail_description || "");
  const reason = getDisplayReason(item);
  return description && description !== reason ? description : "";
}

function getDefaultStatusFilter(focusMode) {
  return focusMode === "documents" ? "open" : "all";
}

function getBoardTitle(focusMode) {
  return focusMode === "documents" ? "Fälle & Aufgaben" : "Nachrichten & Aufgaben";
}

function getBoardSubtitle(focusMode) {
  return focusMode === "documents"
    ? "Unterlagenanforderungen erscheinen hier als konkrete Fälle mit Frist, Status und nächstem Schritt."
    : "Absender, Betreff, Frist, Status und Aktion stehen direkt in einer ruhigen Board-Liste.";
}

function getItemTargetHref(item, focusMode) {
  if (!item) return focusMode === "documents" ? "/dashboard/applicant/filing-requests" : "/dashboard/applicant/nachrichten";
  return item.direct_href || item.href || (focusMode === "documents" ? "/dashboard/applicant/filing-requests" : "/dashboard/applicant/nachrichten");
}

function getNextStepLabel(item, focusMode) {
  if (!item) return focusMode === "documents" ? "Fall prüfen" : "Nachricht prüfen";
  if (item.next_step_label) return item.next_step_label;
  const targetHref = getItemTargetHref(item, focusMode);
  if (item.capabilities?.can_respond) return "Rückmeldung senden";
  if (targetHref.includes("/dashboard/applicant/sprachtest-b1")) return "Sprachtest B1 fortsetzen";
  if (targetHref.includes("/dashboard/applicant/bewerbung?tab=skills")) return "Skills ergänzen";
  if (targetHref.includes("/dashboard/applicant/bewerbung?tab=cv")) return "CV ergänzen";
  if (targetHref.includes("/dashboard/applicant/edit-profile")) return "Profil ergänzen";
  if (targetHref.includes("/dashboard/account")) return "Konto prüfen";
  if (targetHref.includes("/dashboard/applicant/matches")) return "Chance prüfen";
  if (targetHref.includes("/dashboard/applicant/achievements")) return "Erfolge prüfen";
  if (targetHref.includes("/dashboard/applicant/filing-requests")) return focusMode === "documents" ? "Fall prüfen" : "Unterlagen prüfen";
  if (item.sender_class === "coach") return "Hinweis verstehen";
  if (item.folder === "outbox") return "Stand prüfen";
  if (item.folder === "archive") return "Nur bei Bedarf wieder öffnen";
  if (item.due_state === "overdue") return "Jetzt öffnen";
  if (item.due_state === "due_soon") return "Frist im Blick halten";
  return focusMode === "documents" ? "Fall öffnen" : "Nachricht öffnen";
}

function getRowTimeLabel(item) {
  if (!item) return "Keine Frist";
  if (item.due_at) return `Frist ${formatDateShort(item.due_at)}`;
  return formatDateShort(item.last_activity_at || item.created_at);
}

function getBoardSummary(workspace, focusMode) {
  const line = workspace?.summary?.summary_lines?.[0];
  if (line) return line;
  return focusMode === "documents"
    ? "Die Fall- und Unterlagenlogik läuft jetzt in einer einzigen scanbaren Board-Liste."
    : "Alle Applicant-Nachrichten laufen jetzt in einem einzigen scanbaren Board.";
}

function getEmptyStateText({ focusMode, folder, statusFilter, hasCases }) {
  if (focusMode === "documents" && !hasCases) {
    return "Gerade ist kein aktiver Fall mit offener Unterlagenanforderung sichtbar.";
  }
  if (folder === "archive") return "Im Archiv liegen aktuell keine passenden Einträge.";
  if (folder === "outbox") return "Im Ausgang liegt noch keine gesendete Rückmeldung.";
  if (statusFilter === "done") return "Gerade ist in diesem Bereich nichts als erledigt markiert.";
  return focusMode === "documents"
    ? "Gerade ist keine offene Unterlagenanforderung in diesem Filter sichtbar."
    : "Gerade liegt keine neue Applicant-Nachricht in diesem Filter vor.";
}

function folderOptions(summary = {}) {
  return [
    { key: "new", label: "Neu", count: Number(summary.new_count || 0) },
    { key: "outbox", label: "Ausgang", count: Number(summary.outbox_count || 0) },
    { key: "archive", label: "Archiv", count: Number(summary.archive_count || 0) },
  ];
}

function buildReferencePayload(item, filePath) {
  const userid = getStoredIdentity().userid;
  const today = new Date().toISOString().slice(0, 10);
  const requestText = `${item?.detail_title || ""} ${item?.detail_reason || ""} ${item?.detail_description || ""}`.toLowerCase();
  let referenceType = "other";

  if (requestText.includes("reisepass") || requestText.includes("passkopie") || requestText.includes("passport")) {
    referenceType = "passport_copy";
  } else if (requestText.includes("sprachzertifikat") || requestText.includes("sprachtest")) {
    referenceType = "language_certificate";
  } else if (requestText.includes("abschluss") || requestText.includes("ausbildung")) {
    referenceType = "education_certificate";
  } else if (requestText.includes("gesundheit")) {
    referenceType = "health_certificate";
  } else if (requestText.includes("lebenslauf") || requestText.includes("cv")) {
    referenceType = "resume";
  }

  return {
    userid,
    type: referenceType,
    title: `Filing response: ${item?.detail_title || item?.subject || "Nachweis"}`,
    source: "Applicant messaging workspace",
    issued_at: today,
    reference_text: item?.detail_reason || item?.detail_description || item?.subject || "Nachweis für einen Filing-Fall",
    notes: `Filing case ${item?.case_id || "n/a"} / request ${item?.request_id || "n/a"}`,
    status: "pending_review",
    file_path: filePath,
  };
}

function buildCoachQuestion(item, focusMode) {
  if (focusMode === "documents") {
    return `Erkläre mir diese Anforderung verständlich: ${getDisplaySubject(item) || "diese Anforderung"}. Was wird verlangt, warum ist das wichtig und was ist mein nächster Klick?`;
  }
  return `Erkläre mir diese Nachricht verständlich: ${getDisplaySubject(item) || "diese Nachricht"}. Was bedeutet sie und was ist mein nächster sinnvoller Schritt?`;
}

function buildCoachMessageSummary(item, focusMode) {
  const statusLabels = Array.isArray(item?.status_tags)
    ? item.status_tags.map((tag) => String(tag?.label || "").trim()).filter(Boolean).slice(0, 4)
    : [];
  const dueRelative = item?.due_at ? formatRelativeDue(item.due_at) : "";
  return {
    key: item?.message_key || "",
    title: getDisplaySubject(item),
    text: getDisplayPreview(item),
    href: getItemTargetHref(item, focusMode),
    sender_name: getSenderLabel(item),
    sender_role: item?.sender_role || "Absender",
    next_step_label: getNextStepLabel(item, focusMode),
    due_label: item?.due_at ? getRowTimeLabel(item) : "",
    due_relative: dueRelative && dueRelative !== "ohne Frist" ? dueRelative : "",
    status_labels: statusLabels,
  };
}

function buildMessagingCoachSurfaceContext({
  focusMode,
  folder,
  statusFilter,
  expandedMessageKey,
  selectedCaseId,
  filteredItems,
  loading,
  error,
  workspace,
  focusedItem,
}) {
  const prioritizedItem = focusedItem || filteredItems[0] || null;
  const orderedItems = focusedItem
    ? [focusedItem]
    : prioritizedItem
      ? [prioritizedItem, ...filteredItems.filter((item) => item.message_key !== prioritizedItem.message_key)]
      : filteredItems;
  const visibleMessages = orderedItems.slice(0, focusedItem ? 1 : 4).map((item) => buildCoachMessageSummary(item, focusMode));
  const coachNextStep = prioritizedItem
    ? {
        key: prioritizedItem.message_key,
        label_de: getDisplaySubject(prioritizedItem) || "Nachricht öffnen",
        label_en: focusMode === "documents" ? "open this case" : "open this message",
        reason_de: getDisplayPreview(prioritizedItem) || "Hier liegt gerade dein nächster sichtbarer Schritt.",
        href: prioritizedItem.href || "/dashboard/applicant/nachrichten",
      }
    : {
        key: focusMode === "documents" ? "documents_idle" : "messages_idle",
        label_de: focusMode === "documents" ? "Fälle prüfen" : "Postfach prüfen",
        label_en: focusMode === "documents" ? "review your cases" : "review your inbox",
        reason_de: "Gerade liegt in diesem Applicant-Bereich kein sichtbarer offener Punkt obenauf.",
        href: focusMode === "documents" ? "/dashboard/applicant/filing-requests" : "/dashboard/applicant/nachrichten",
      };

  return {
    screen_key: focusMode === "documents" ? "applicant_filing_requests" : "applicant_messages",
    screen_label: focusMode === "documents" ? "Unterlagen" : "Nachrichten",
    focused_message_key: prioritizedItem?.message_key || "",
    highlighted_step: prioritizedItem ? `${getDisplaySubject(prioritizedItem)}: ${getDisplayPreview(prioritizedItem)}` : coachNextStep.reason_de,
    surface_state_key: [
      focusMode,
      folder,
      statusFilter,
      expandedMessageKey || "none",
      prioritizedItem?.message_key || "none",
      selectedCaseId || "all",
      filteredItems.length,
      loading ? "loading" : "ready",
      error ? "error" : "ok",
    ].join(":"),
    surface_summary: {
      surface_type: focusMode === "documents" ? "documents" : "messages",
      status_label: loading
        ? "Wird geladen"
        : filteredItems.length
          ? `${filteredItems.length} sichtbare Einträge`
          : "Keine sichtbaren Einträge",
      status_note: loading
        ? "Der Applicant-Arbeitsraum wird gerade geladen."
        : getBoardSummary(workspace, focusMode),
      prioritized_message: prioritizedItem ? buildCoachMessageSummary(prioritizedItem, focusMode) : null,
      visible_messages: visibleMessages,
      coach_next_step: coachNextStep,
      coach_missing_items: orderedItems.slice(0, focusedItem ? 1 : 4).map((item) => ({
        key: item.message_key,
        label_de: getDisplaySubject(item) || "Applicant-Nachricht",
        label_en: focusMode === "documents" ? "case item" : "applicant message",
        reason_de: getDisplayPreview(item) || "Hier liegt ein sichtbarer Applicant-Hinweis für dich.",
        href: item.href || "/dashboard/applicant/nachrichten",
      })),
    },
  };
}

export default function ApplicantMessagingWorkspace({ focusMode = "messages" }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { registerCoachSurface, setCoachOpen, askApplicantCoach } = useApplicantCoach();
  const userid = getStoredIdentity().userid || "";
  const [workspace, setWorkspace] = useState(EMPTY_WORKSPACE);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyMessageKey, setBusyMessageKey] = useState("");
  const [busyRequestId, setBusyRequestId] = useState("");
  const [coachFocusMessageKey, setCoachFocusMessageKey] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  const folder = folderOptions(workspace.summary).some((entry) => entry.key === searchParams.get("box"))
    ? searchParams.get("box")
    : workspace.summary?.default_folder || "new";
  const statusFilter = ["all", "open", "done"].includes(searchParams.get("status"))
    ? searchParams.get("status")
    : getDefaultStatusFilter(focusMode);
  const selectedCaseId = focusMode === "documents" && Number(searchParams.get("case_id") || 0) > 0
    ? Number(searchParams.get("case_id"))
    : null;
  const expandedMessageKey = searchParams.get("message") || "";

  useEffect(() => {
    document.title = focusMode === "documents"
      ? "Experts4Europe | Fälle & Aufgaben"
      : "Experts4Europe | Nachrichten & Aufgaben";
  }, [focusMode]);

  const updateSearch = useCallback((mutate) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const ensureDraft = useCallback((requestId) => drafts[String(requestId)] || createDraft(), [drafts]);

  const patchDraft = useCallback((requestId, key, value) => {
    setDrafts((current) => ({
      ...current,
      [String(requestId)]: {
        ...createDraft(),
        ...(current[String(requestId)] || {}),
        [key]: value,
      },
    }));
  }, []);

  const resetDraft = useCallback((requestId) => {
    setDrafts((current) => ({
      ...current,
      [String(requestId)]: createDraft(),
    }));
  }, []);

  const handleAuthError = useCallback((err) => {
    const status = Number(err?.response?.status || err?.status);
    if (status !== 401) return false;

    clearStoredAuth();
    const detail = String(err?.response?.data?.detail || err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivität automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gültig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  }, []);

  const loadWorkspace = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const data = await loadApplicantMessagingWorkspace();
      setWorkspace({
        ...EMPTY_WORKSPACE,
        ...(data || {}),
      });
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || err?.message || "Der Applicant-Arbeitsraum konnte nicht geladen werden.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const baseItems = useMemo(() => (
    focusMode === "documents"
      ? (workspace.items || []).filter(isDocumentItem)
      : (workspace.items || [])
  ), [focusMode, workspace.items]);

  const caseLookup = useMemo(() => (
    new Map((workspace.cases || []).map((entry) => [Number(entry.case_id), entry]))
  ), [workspace.cases]);

  const filteredItems = useMemo(() => {
    return baseItems.filter((item) => {
      if (item.folder !== folder) return false;
      if (statusFilter === "open" && item.task_status !== "open") return false;
      if (statusFilter === "done" && item.task_status !== "done") return false;
      if (focusMode === "documents" && selectedCaseId && Number(item.case_id || 0) !== Number(selectedCaseId)) return false;
      return true;
    });
  }, [baseItems, folder, focusMode, selectedCaseId, statusFilter]);
  const prioritizedItem = filteredItems[0] || null;
  const transactionStatusBand = [
    {
      label: "Sichtbare Eintraege",
      value: loading ? "Wird geladen" : `${filteredItems.length} sichtbar`,
      note: loading ? "Der Applicant-Arbeitsraum wird gerade geladen." : getBoardSummary(workspace, focusMode),
    },
    {
      label: "Offen",
      value: `${Number(workspace.summary?.open_count || 0)} offen`,
      note: `${Number(workspace.summary?.done_count || 0)} erledigt · ${Number(workspace.summary?.archive_count || 0)} im Archiv`,
    },
    {
      label: "Naechste Frist",
      value: prioritizedItem?.due_at ? formatDateTime(prioritizedItem.due_at) : "Keine offene Frist",
      note: prioritizedItem ? getDisplaySubject(prioritizedItem) : "Sobald ein Fall im Fokus steht, erscheint seine Frist hier.",
    },
    {
      label: "Naechster Schritt",
      value: prioritizedItem ? getNextStepLabel(prioritizedItem, focusMode) : (focusMode === "documents" ? "Fall pruefen" : "Postfach pruefen"),
      note: prioritizedItem ? getDisplayPreview(prioritizedItem) : "Gerade liegt kein sichtbarer Eintrag obenauf.",
    },
  ];

  const expandedItem = useMemo(() => (
    filteredItems.find((item) => item.message_key === expandedMessageKey)
    || baseItems.find((item) => item.message_key === expandedMessageKey)
    || null
  ), [baseItems, expandedMessageKey, filteredItems]);

  const focusedCoachItem = useMemo(() => (
    filteredItems.find((item) => item.message_key === coachFocusMessageKey) || null
  ), [coachFocusMessageKey, filteredItems]);

  const notifications = useMemo(() => (
    (workspace.highlights || []).map((entry) => ({
      key: entry.key,
      title: entry.title,
      text: entry.text,
      href: entry.href,
    }))
  ), [workspace.highlights]);

  const searchItems = useMemo(() => {
    const items = [
      { href: "/dashboard/applicant", label: "Dashboard", description: "Mission Control und Priorisierung" },
      { href: "/dashboard/applicant/nachrichten", label: "Nachrichten", description: "Kommunikation, Aufgaben und Fristen" },
      { href: "/dashboard/applicant/filing-requests", label: "Unterlagen", description: "Fälle, Anforderungen und Einreichungen" },
    ];

    (workspace.highlights || []).forEach((entry) => {
      if (!entry?.href || !entry?.title) return;
      items.push({
        href: entry.href,
        label: entry.title,
        description: entry.text || "Wichtiger Applicant-Hinweis",
      });
    });

    (workspace.cases || []).slice(0, 6).forEach((entry) => {
      items.push({
        href: entry.primary_href,
        label: entry.title,
        description: `${entry.employer_name} · ${entry.workflow_stage_label}`,
      });
    });

    return items;
  }, [workspace.cases, workspace.highlights]);

  const coachSurfaceContext = useMemo(() => buildMessagingCoachSurfaceContext({
    focusMode,
    folder,
    statusFilter,
    expandedMessageKey,
    selectedCaseId,
    filteredItems,
    loading,
    error,
    workspace,
    focusedItem: focusedCoachItem || expandedItem || filteredItems[0] || null,
  }), [error, expandedItem, expandedMessageKey, filteredItems, focusMode, folder, focusedCoachItem, loading, selectedCaseId, statusFilter, workspace]);

  useEffect(() => {
    registerCoachSurface(coachSurfaceContext);
  }, [coachSurfaceContext, registerCoachSurface]);

  useEffect(() => {
    if (coachFocusMessageKey && !focusedCoachItem) {
      setCoachFocusMessageKey("");
    }
  }, [coachFocusMessageKey, focusedCoachItem]);

  const applyMessageAction = useCallback(async ({ item, action, payload = {}, successMessage = "" }) => {
    if (!item?.message_key) return null;
    setBusyMessageKey(item.message_key);
    setError("");
    if (successMessage) setNotice("");

    try {
      const nextWorkspace = await updateApplicantMessageState({
        message_key: item.message_key,
        action,
        ...payload,
      });
      setWorkspace({
        ...EMPTY_WORKSPACE,
        ...(nextWorkspace || {}),
      });
      if (successMessage) setNotice(successMessage);
      return nextWorkspace;
    } catch (err) {
      if (handleAuthError(err)) return null;
      setError(err?.response?.data?.message || err?.message || "Die Nachrichtenaktion konnte nicht gespeichert werden.");
      return null;
    } finally {
      setBusyMessageKey("");
    }
  }, [handleAuthError]);

  const toggleExpand = useCallback(async (item) => {
    if (!item) return;
    const closing = expandedMessageKey === item.message_key;
    updateSearch((next) => {
      if (closing) {
        next.delete("message");
      } else {
        next.set("message", item.message_key);
        if (focusMode === "documents" && item.case_id) {
          next.set("case_id", String(item.case_id));
        }
      }
    });

    if (!closing && item.read_status === "unread") {
      await applyMessageAction({ item, action: "open" });
    }
  }, [applyMessageAction, expandedMessageKey, focusMode, updateSearch]);

  const handleCoach = useCallback(async (item) => {
    if (!item) return;
    const itemCoachSurfaceContext = buildMessagingCoachSurfaceContext({
      focusMode,
      folder,
      statusFilter,
      expandedMessageKey,
      selectedCaseId,
      filteredItems,
      loading,
      error,
      workspace,
      focusedItem: item,
    });
    setCoachFocusMessageKey(item.message_key);
    setCoachOpen(true);
    await askApplicantCoach({
      question: buildCoachQuestion(item, focusMode),
      quickAction: "",
      surfaceContext: itemCoachSurfaceContext,
    });
  }, [askApplicantCoach, error, expandedMessageKey, filteredItems, focusMode, folder, loading, selectedCaseId, setCoachOpen, statusFilter, workspace]);

  const uploadProof = useCallback(async (item, file) => {
    if (!file || !userid || !item?.request_id) return;
    setBusyRequestId(String(item.request_id));
    setError("");
    setNotice("");
    try {
      const formData = new FormData();
      formData.append("userid", userid);
      formData.append("file", file);
      const uploadRes = await axios.post(`${config.API_BASE_URL}/api/cv/verification-references/upload`, formData, {
        headers: {
          ...authHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      const referenceRes = await axios.post(
        `${config.API_BASE_URL}/api/cv/verification-references`,
        buildReferencePayload(item, uploadRes.data?.filePath || ""),
        { headers: authHeaders() }
      );
      const reference = referenceRes.data?.reference;
      patchDraft(item.request_id, "verification_reference_id", reference?.id || null);
      patchDraft(item.request_id, "uploaded_reference_label", reference?.title || uploadRes.data?.originalName || "Nachweis hochgeladen");
      setNotice("Nachweis hochgeladen und für diesen Fall vorgemerkt.");
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || err?.message || "Der Nachweis konnte nicht hochgeladen werden.");
    } finally {
      setBusyRequestId("");
    }
  }, [handleAuthError, patchDraft, userid]);

  const submitResponse = useCallback(async (item) => {
    if (!item?.request_id || !item?.case_id) return;
    const draft = ensureDraft(item.request_id);
    const hasMeaningfulInput = Boolean(
      draft.response_text.trim()
      || draft.additional_details.trim()
      || draft.acknowledged
      || draft.mark_not_available
      || draft.mark_already_fulfilled
      || draft.verification_reference_id
    );

    if (!hasMeaningfulInput) {
      setError("Bitte trage mindestens eine Rückmeldung, Bestätigung oder einen Nachweis ein.");
      return;
    }

    setBusyRequestId(String(item.request_id));
    setError("");
    setNotice("");
    try {
      await axios.post(
        `${config.API_BASE_URL}/api/applicant/authority-filing/requests/${item.request_id}/responses`,
        {
          case_id: item.case_id,
          response_text: draft.response_text,
          structured_fields: {
            additional_details: draft.additional_details,
            applicant_acknowledgement: draft.acknowledged ? "confirmed" : "",
            current_availability: draft.mark_not_available ? "currently_not_available" : "",
          },
          acknowledged: draft.acknowledged,
          mark_not_available: draft.mark_not_available,
          mark_already_fulfilled: draft.mark_already_fulfilled,
          verification_reference_id: draft.verification_reference_id,
          completion_note: draft.completion_note,
        },
        { headers: authHeaders() }
      );
      resetDraft(item.request_id);
      setNotice(`Rückmeldung gespeichert: ${getDisplaySubject(item)}`);
      await loadWorkspace({ silent: true });
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || err?.message || "Die Rückmeldung konnte nicht gespeichert werden.");
    } finally {
      setBusyRequestId("");
    }
  }, [ensureDraft, handleAuthError, loadWorkspace, resetDraft]);

  const renderRowAction = (item, {
    key,
    icon: Icon,
    label,
    onClick,
    disabled = false,
    accent = "default",
  }) => {
    const accentClass = accent === "danger"
      ? "applicant-transaction-action applicant-transaction-action--danger"
      : accent === "primary"
        ? "applicant-transaction-action applicant-transaction-action--primary"
        : "applicant-transaction-action";

    return (
      <button
        key={`${item?.message_key || "item"}-${key}`}
        type="button"
        className={accentClass}
        onClick={onClick}
        disabled={disabled}
      >
        <Icon size={13} />
        <span>{label}</span>
      </button>
    );
  };

  const renderRequestComposer = (item) => {
    if (!item?.capabilities?.can_respond || focusMode !== "documents") return null;
    const draft = ensureDraft(item.request_id);
    const busy = busyRequestId === String(item.request_id);

    return (
      <section className="applicant-transaction-composer">
        <div className="applicant-transaction-header__top">
          <div>
            <p className="e4e-status-band__label">Rueckmeldung zu diesem Fall</p>
            <h4 className="applicant-transaction-row__subject">Was du jetzt einreichen oder kurz erklaeren kannst</h4>
          </div>
          <span className="applicant-transaction-chip">
            Frist {formatDateShort(item.due_at)}
          </span>
        </div>

        <div className="applicant-transaction-composer__grid">
          <div className="applicant-transaction-composer__field">
            <div>
              <Label className="e4e-status-band__label">Deine Rueckmeldung</Label>
              <Textarea
                rows={4}
                value={draft.response_text}
                onChange={(event) => patchDraft(item.request_id, "response_text", event.target.value)}
                className="mt-1 border-slate-200 bg-white"
                placeholder="Beschreibe kurz, was du nachreichst oder was zu diesem Fall wichtig ist."
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="e4e-status-band__label">Ergaenzende Angabe</Label>
                <Input
                  value={draft.additional_details}
                  onChange={(event) => patchDraft(item.request_id, "additional_details", event.target.value)}
                  className="mt-1 border-slate-200 bg-white"
                  placeholder="z. B. Datum oder Bearbeitungsstand"
                />
              </div>
              <div>
                <Label className="e4e-status-band__label">Kurze Notiz</Label>
                <Input
                  value={draft.completion_note}
                  onChange={(event) => patchDraft(item.request_id, "completion_note", event.target.value)}
                  className="mt-1 border-slate-200 bg-white"
                  placeholder="z. B. kurze Zusatzinfo für diesen Fall"
                />
              </div>
            </div>
          </div>

          <div className="applicant-transaction-composer__aside">
            <p className="e4e-status-band__label">Schnellantwort</p>
            <div className="applicant-transaction-composer__checks text-sm text-slate-700">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={draft.acknowledged}
                  onChange={(event) => patchDraft(item.request_id, "acknowledged", event.target.checked)}
                />
                <span>Ich habe die Anforderung gelesen und bestätige sie.</span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={draft.mark_not_available}
                  onChange={(event) => patchDraft(item.request_id, "mark_not_available", event.target.checked)}
                />
                <span>Das liegt aktuell noch nicht vor.</span>
              </label>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={draft.mark_already_fulfilled}
                  onChange={(event) => patchDraft(item.request_id, "mark_already_fulfilled", event.target.checked)}
                />
                <span>Ich habe das bereits erledigt und verweise auf einen Nachweis.</span>
              </label>
            </div>

            <div className="applicant-transaction-composer__upload">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <FileText size={16} />
                <span>Nachweis hochladen</span>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Der Upload bleibt im Verification-Bereich. Im Fall wird nur die referenzierte Datei genutzt.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Label className="inline-flex cursor-pointer items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700">
                  {busy ? "Lädt..." : "Datei auswählen"}
                  <input
                    type="file"
                    accept=".pdf,image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(event) => uploadProof(item, event.target.files?.[0])}
                  />
                </Label>
                {draft.uploaded_reference_label ? (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                    {draft.uploaded_reference_label}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="applicant-transaction-detail__footer applicant-transaction-composer__actions">
          <p className="text-sm text-slate-600">
            Du sendest hier nur eine fallbezogene Rueckmeldung. Keine automatische Freigabe und keine versteckte Statusaenderung.
          </p>
          <Button className="applicant-cta-button" onClick={() => submitResponse(item)} disabled={busy}>
            {busy ? "Speichert..." : "Rueckmeldung speichern"}
          </Button>
        </div>
      </section>
    );
  };

  const renderExpandedContent = (item) => {
    if (!item) return null;
    const caseEntry = item.case_id ? caseLookup.get(Number(item.case_id)) || null : null;
    const detailCards = [
      {
        key: "reason",
        title: "Warum diese Nachricht wichtig ist",
        text: getDisplayReason(item),
      },
      {
        key: "description",
        title: "Was du jetzt tun kannst",
        text: getDisplayDescription(item),
      },
    ].filter((entry) => entry.text);

    if (item.response_preview && !detailCards.find((entry) => entry.key === "description")) {
      detailCards.push({
        key: "response_preview",
        title: "Deine letzte Rückmeldung",
        text: item.response_preview,
      });
    }

    const metadata = [
      { key: "sender", label: "Absender", value: getSenderLabel(item) },
      { key: "role", label: "Rolle", value: item.sender_role || "Absender" },
      { key: "time", label: "Zeitpunkt", value: formatDateTime(item.last_activity_at || item.created_at) },
      { key: "due", label: "Frist", value: formatDateTime(item.due_at) },
      { key: "next", label: "Nächster Schritt", value: getNextStepLabel(item, focusMode) },
    ];

    if (caseEntry) {
      metadata.push({
        key: "case",
        label: "Fall",
        value: `${caseEntry.title} · ${caseEntry.employer_name}`,
      });
    }

    return (
      <div className="applicant-transaction-detail">
        <div className="applicant-transaction-detail__head">
          <p className="e4e-status-band__label">
            {focusMode === "documents" ? "Fall im Detail" : "Nachricht im Detail"}
          </p>
          <h3>{getDisplaySubject(item)}</h3>
          <p>{getDisplayPreview(item)}</p>
        </div>

        <div className="applicant-transaction-detail__meta">
          {metadata.map((entry) => (
            <div
              key={`${item.message_key}-${entry.key}`}
              className="applicant-transaction-chip"
            >
              <span className="e4e-status-band__label">{entry.label}</span>
              <span>{entry.value}</span>
            </div>
          ))}
        </div>

        {detailCards.length ? (
          <div className="applicant-transaction-detail__cards">
            {detailCards.map((entry) => (
              <div key={`${item.message_key}-${entry.key}`} className="applicant-transaction-detail__card">
                <p className="e4e-status-band__label">{entry.title}</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">{entry.text}</p>
              </div>
            ))}
          </div>
        ) : null}

        {renderRequestComposer(item)}
      </div>
    );
  };

  return (
    <ApplicantShell
      title={focusMode === "documents" ? "Faelle & Aufgaben" : "Nachrichten & Aufgaben"}
      subtitle={focusMode === "documents"
        ? "Pruefe Frist, Status und naechsten Schritt pro Fall in einer geordneten Evidenzflaeche."
        : "Absender, Status und Folgehandlungen bleiben in einem ruhigen Applicant-Postfach sichtbar."}
      notifications={notifications}
      searchItems={searchItems}
    >
      <div className="applicant-transaction-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        {notice ? <div className="dashboard-inline-alert dashboard-inline-alert-success">{notice}</div> : null}
        <section className="e4e-status-band applicant-transaction-status-band">
          <div className="e4e-status-band__grid">
            {transactionStatusBand.map((item) => (
              <article key={item.label} className="e4e-status-band__item">
                <span className="e4e-status-band__label">{item.label}</span>
                <strong className="e4e-status-band__value">{item.value}</strong>
                <small className="e4e-status-band__note">{item.note}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="applicant-transaction-header">
          <div className="applicant-transaction-header__top">
            <div className="applicant-transaction-header__copy">
              <p className="e4e-status-band__label">
                {focusMode === "documents" ? "Applicant-Fälle" : "Applicant-Postfach"}
              </p>
              <h2>{getBoardTitle(focusMode)}</h2>
              <p>{getBoardSubtitle(focusMode)}</p>
            </div>

            <div className="applicant-transaction-row__actions">
              <span className="applicant-transaction-chip">
                {loading ? "Wird geladen" : `${filteredItems.length} sichtbare Einträge`}
              </span>
              <button
                type="button"
                className="applicant-transaction-action"
                onClick={() => loadWorkspace()}
                disabled={loading}
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                <span>Aktualisieren</span>
              </button>
            </div>
          </div>

          <p className="applicant-transaction-refresh-note">{getBoardSummary(workspace, focusMode)}</p>

          <div className="applicant-transaction-filter-row">
            {folderOptions(workspace.summary).map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={`applicant-transaction-filter${folder === entry.key ? " is-active" : ""}`}
                onClick={() => updateSearch((next) => {
                  next.set("box", entry.key);
                  next.set("status", entry.key === "archive" ? "all" : getDefaultStatusFilter(focusMode));
                  next.delete("message");
                })}
              >
                <span>{entry.label}</span>
                <span className="applicant-transaction-chip">{entry.count}</span>
              </button>
            ))}

            <div className="applicant-transaction-row__actions">
              {["all", "open", "done"].map((entry) => (
                <button
                  key={entry}
                  type="button"
                  className={`applicant-transaction-filter${statusFilter === entry ? " is-active" : ""}`}
                  onClick={() => updateSearch((next) => {
                    next.set("status", entry);
                    next.delete("message");
                  })}
                >
                  {getStatusFilterLabel(entry)}
                </button>
              ))}
            </div>
          </div>

          {focusMode === "documents" && (workspace.cases || []).length ? (
            <div className="applicant-transaction-case-row">
              <button
                type="button"
                className={`applicant-transaction-case-filter${!selectedCaseId ? " is-active" : ""}`}
                onClick={() => updateSearch((next) => {
                  next.delete("case_id");
                  next.delete("message");
                })}
              >
                Alle Fälle
              </button>
              {(workspace.cases || []).map((entry) => (
                <button
                  key={`case-${entry.case_id}`}
                  type="button"
                  className={`applicant-transaction-case-filter${selectedCaseId === Number(entry.case_id) ? " is-active" : ""}`}
                  onClick={() => updateSearch((next) => {
                    next.set("case_id", String(entry.case_id));
                    next.delete("message");
                  })}
                >
                  <span className="truncate">{entry.title}</span>
                  <span className="applicant-transaction-chip">{entry.open_count}</span>
                </button>
              ))}
            </div>
          ) : null}
        </section>

        <section className="applicant-transaction-board">
          <div>
            {loading ? (
              <div className="px-5 py-10 text-sm text-slate-500">Der Applicant-Arbeitsraum wird geladen ...</div>
            ) : filteredItems.length ? filteredItems.map((item) => {
              const isExpanded = expandedMessageKey === item.message_key;
              const isBusy = busyMessageKey === item.message_key;

              return (
                <article key={item.message_key} className={`applicant-transaction-row${isExpanded ? " is-expanded" : ""}`}>
                  <div className="applicant-transaction-row__summary">
                      <div className="min-w-0">
                        <div className="applicant-transaction-row__title">
                          <span className={senderTone(item.sender_class)}>
                            {getSenderLabel(item)}
                          </span>
                          <span className="e4e-status-band__label">
                            {item.sender_role || "Absender"}
                          </span>
                          {focusMode === "documents" && item.case_id ? (
                            <span className="applicant-transaction-chip">
                              {caseLookup.get(Number(item.case_id))?.title || `Fall ${item.case_id}`}
                            </span>
                          ) : null}
                          <strong className="applicant-transaction-row__subject min-w-0 flex-1 truncate">
                            {getDisplaySubject(item)}
                          </strong>
                        </div>

                        <div className="applicant-transaction-row__preview">
                          <span>{getDisplayPreview(item)}</span>
                        </div>
                      </div>

                      <div className="applicant-transaction-meta">
                        <span className="applicant-transaction-chip">{getRowTimeLabel(item)}</span>
                        {item.due_at ? <span className="applicant-transaction-chip">{formatRelativeDue(item.due_at)}</span> : null}
                        <span className="applicant-transaction-chip">{getNextStepLabel(item, focusMode)}</span>
                        <div className="applicant-transaction-tags">
                          {item.status_tags?.slice(0, 2).map((tag) => (
                            <span key={`${item.message_key}-${tag.key}`} className={toneClass(tag.tone)}>
                            {tag.label}
                          </span>
                          ))}
                        </div>
                      </div>

                      <div className="applicant-transaction-row__actions">
                        {renderRowAction(item, {
                          key: "open",
                          icon: isExpanded ? ChevronUp : ChevronDown,
                          label: isExpanded ? "Schließen" : "Öffnen",
                          onClick: () => toggleExpand(item),
                          disabled: isBusy,
                          accent: "primary",
                        })}
                        {renderRowAction(item, {
                          key: "coach",
                          icon: Bot,
                          label: "Einordnen",
                          onClick: () => handleCoach(item),
                        })}
                        {item.capabilities?.can_archive ? renderRowAction(item, {
                          key: "archive",
                          icon: Archive,
                          label: "Archiv",
                          onClick: () => applyMessageAction({
                            item,
                            action: "archive",
                            successMessage: "Eintrag wurde archiviert.",
                          }),
                          disabled: isBusy,
                        }) : null}
                        {item.capabilities?.can_restore ? renderRowAction(item, {
                          key: "restore",
                          icon: ArchiveRestore,
                          label: "Zurückholen",
                          onClick: () => applyMessageAction({
                            item,
                            action: "restore",
                            successMessage: "Eintrag wurde wiederhergestellt.",
                          }),
                          disabled: isBusy,
                        }) : null}
                        {renderRowAction(item, {
                          key: "delete",
                          icon: Trash2,
                          label: "Löschen",
                          onClick: () => applyMessageAction({
                            item,
                            action: "delete",
                            successMessage: "Eintrag wurde gelöscht.",
                          }),
                          disabled: isBusy,
                          accent: "danger",
                        })}
                      </div>
                  </div>

                  {isExpanded ? renderExpandedContent(item) : null}
                </article>
              );
            }) : (
              <div className="px-5 py-10">
                <div className="flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                  {focusMode === "documents" ? <FileText size={18} /> : <MessageSquareMore size={18} />}
                  <span>{getEmptyStateText({ focusMode, folder, statusFilter, hasCases: (workspace.cases || []).length > 0 })}</span>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>
              Startseite
            </Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>
              Erneut anmelden
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ApplicantCoachPanel />
    </ApplicantShell>
  );
}
